import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Radio, Users as UsersIcon } from "lucide-react";

const ROLE_LABEL = {
  super_admin: "SUPER ADMIN",
  direktur: "DIREKTUR",
  manager: "MANAGER",
  admin: "ADMIN",
};

const ROLE_COLOR = {
  super_admin: "status-hot",
  direktur: "status-followup",
  manager: "status-warm",
  admin: "status-cold",
};

function relativeTime(iso) {
  if (!iso) return "-";
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 10) return "baru saja";
  if (diff < 60) return `${diff} detik lalu`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m} menit lalu`;
  const h = Math.floor(m / 60);
  return `${h} jam lalu`;
}

export default function OnlineUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const fetchOnline = async () => {
    try {
      const { data } = await api.get("/presence/online");
      setUsers(data);
    } catch (e) {
      // ignore transient errors
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnline();
    timer.current = setInterval(fetchOnline, 15000); // poll every 15s
    return () => clearInterval(timer.current);
  }, []);

  return (
    <div className="space-y-6" data-testid="online-users-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Monitoring</div>
          <h1 className="font-heading text-3xl text-white font-bold tracking-tight mt-1">Online Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pengguna yang sedang membuka aplikasi (aktif dalam 60 detik terakhir).
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#32D74B]/10 border border-[#32D74B]/30">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32D74B] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#32D74B]" />
          </span>
          <span data-testid="online-count" className="text-sm font-mono text-[#32D74B]">
            {users.length} online
          </span>
        </div>
      </div>

      <Card className="bg-[#121212] border-border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Memuat...</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <UsersIcon className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" strokeWidth={1.5} />
            <div className="text-sm text-muted-foreground">Belum ada pengguna online saat ini.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  <th className="text-left font-medium px-4 py-3">Nama</th>
                  <th className="text-left font-medium px-4 py-3">Role</th>
                  <th className="text-left font-medium px-4 py-3">Email</th>
                  <th className="text-left font-medium px-4 py-3">Terakhir Aktif</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`online-user-${u.user_id}`} className="border-b border-border/50 hover:bg-[#1C1C1E]/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary font-heading font-semibold text-xs">
                          {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <span className="text-white font-medium">{u.name || "-"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${ROLE_COLOR[u.role] || "status-cold"} border-0 text-[10px]`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{relativeTime(u.last_seen)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-[#32D74B] text-xs">
                        <Radio className="w-3.5 h-3.5" strokeWidth={2} />
                        Online
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
